import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import ActivityActionsModal from "../components/ActivityActionsModal";
import RouteReplayModal from "../components/RouteReplayModal";
import TripGpsTrailModal from "../components/TripGpsTrailModal";
import FleetMap from "../components/FleetMap";
import { useAuth } from "../context/AuthContext";
import { num, litres } from "../lib/format";
import { useCountry, countryInfo } from "../context/CountryContext";
import {
  Truck as TruckIcon, ArrowRight, MagnifyingGlass, ArrowsClockwise,
  CircleNotch, Clock, GasPump, ClipboardText, FlowArrow, Check, X, FileText, Plus, MapPin,
} from "@phosphor-icons/react";

// Activity status → column bucket
const COLUMNS = [
  {
    key: "needs_action",
    label: "Needs action",
    hint: "Approver must review fuel requests",
    statuses: ["requested"],
    border: "border-l-ps-red",
    headerCls: "bg-red-50 text-ps-red border-red-200",
    icon: ClipboardText,
  },
  {
    key: "in_transit",
    label: "On the road",
    hint: "Fuel issued · truck rolling",
    statuses: ["approved", "issued"],
    border: "border-l-amber-500",
    headerCls: "bg-amber-50 text-ps-orange border-amber-200",
    icon: TruckIcon,
  },
  {
    key: "awaiting_paper",
    label: "Awaiting waybill / reconcile",
    hint: "CMR or end-of-shift trip count pending",
    statuses: ["awaiting_waybill"],
    border: "border-l-indigo-500",
    headerCls: "bg-indigo-50 text-indigo-700 border-indigo-200",
    icon: FlowArrow,
  },
  {
    key: "done_today",
    label: "Done today",
    hint: "Completed in the last 24 h",
    statuses: ["completed"],
    border: "border-l-lime-500",
    headerCls: "bg-lime-50 text-lime-700 border-lime-200",
    icon: GasPump,
    recentOnly: true, // filter to last 24h
  },
];

const STATUS_CHIPS = {
  requested:         { label: "Requested",       cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved:          { label: "Approved",        cls: "bg-sky-50 text-ps-blue border-sky-200" },
  issued:            { label: "Fuel issued",     cls: "bg-orange-50 text-ps-orange border-orange-200" },
  awaiting_waybill:  { label: "Awaiting waybill", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  completed:         { label: "Completed",       cls: "bg-lime-50 text-lime-700 border-lime-200" },
};

// Activity-type label
const TYPE_LABEL = {
  loading: "Loading",
  evacuation: "Evacuation",
  trip: "Trip",
  other: "Other",
};

const REFRESH_INTERVAL_MS = 30_000;

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60)     return `${diffSec}s ago`;
  if (diffSec < 3600)   return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400)  return `${Math.floor(diffSec / 3600)} h ago`;
  return `${Math.floor(diffSec / 86400)} d ago`;
}

function rowLastUpdate(r) {
  // Use the most recent of any timestamp we have
  return r.completed_at || r.issued_at || r.approved_at || r.created_at || r.date;
}

export default function ControlTower() {
  const { scope, filterParam, isAll } = useCountry();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canApprove = ["admin", "fuel_approver", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [, setTick] = useState(0); // re-render every 30s for relative time
  const timerRef = useRef(null);
  // inline action modal state
  const [actionState, setActionState] = useState(null); // { activity, mode }
  const [trailActivityId, setTrailActivityId] = useState(null);   // opens the trip-GPS-trail modal
  const startAction = (activity, mode) => setActionState({ activity, mode });
  const closeAction = () => setActionState(null);
  // Live GPS positions — keyed by vehicle_number for quick lookup on each card
  const [gpsByPlate, setGpsByPlate] = useState({});

  // Navigate to /activities with deep-link query that triggers the right modal there
  // (used for "+ More fuel" and "+ New activity" which need the full create form).
  const goCreate = () => navigate("/activities?action=new");
  const goMoreFuel = (act) => navigate(`/activities?focus=${act.id}&action=more-fuel`);

  const load = async (silent = false) => {
    if (!silent) {
      setLoading(true);
      // Wipe the board immediately when the country scope changes so the operator
      // doesn't see the previous country's cards lingering during the fetch.
      setRows([]);
    }
    try {
      const params = {};
      if (filterParam) params.country = filterParam;
      // Backend returns the latest 500 by default — plenty for a live board
      const [actsRes, gpsRes] = await Promise.all([
        api.get("/activities", { params }),
        api.get("/gps/positions").catch(() => ({ data: { positions: [] } })),
      ]);
      setRows(actsRes.data || []);
      const idx = {};
      (gpsRes.data?.positions || []).forEach((p) => {
        if (p.vehicle_number) idx[p.vehicle_number] = p;
      });
      setGpsByPlate(idx);
      setLastRefresh(new Date());
    } catch {
      // keep previous rows on transient error — better UX than wiping the board
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterParam]);

  // Live polling
  useEffect(() => {
    if (!autoRefresh) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    timerRef.current = setInterval(() => load(true), REFRESH_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    /* eslint-disable-next-line */
  }, [autoRefresh, filterParam]);

  // Refresh relative timestamps every 30s without re-fetching
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // Filter by client search (+ defensive country filter so a mis-tagged activity
  // can never leak into the wrong country scope).
  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let base = rows;
    if (filterParam) {
      base = base.filter((r) => (r.country || "GH") === filterParam);
    }
    if (!needle) return base;
    return base.filter((r) => {
      return (
        (r.vehicle_number || "").toLowerCase().includes(needle) ||
        (r.client || "").toLowerCase().includes(needle) ||
        (r.driver_name || "").toLowerCase().includes(needle) ||
        (r.destination || "").toLowerCase().includes(needle) ||
        (r.origin || "").toLowerCase().includes(needle)
      );
    });
  }, [rows, q, filterParam]);

  // Bucket into columns
  const grouped = useMemo(() => {
    const since24h = Date.now() - 24 * 3600 * 1000;
    const out = {};
    COLUMNS.forEach((c) => { out[c.key] = []; });
    filteredRows.forEach((r) => {
      const col = COLUMNS.find((c) => c.statuses.includes(r.status));
      if (!col) return;
      if (col.recentOnly) {
        const ts = new Date(rowLastUpdate(r)).getTime();
        if (!isFinite(ts) || ts < since24h) return;
      }
      out[col.key].push(r);
    });
    // Sort newest-first within each column
    Object.keys(out).forEach((k) => {
      out[k].sort((a, b) => new Date(rowLastUpdate(b)) - new Date(rowLastUpdate(a)));
    });
    return out;
  }, [filteredRows]);

  // KPIs
  const kpis = useMemo(() => {
    const active = filteredRows.filter((r) => ["requested", "approved", "issued", "awaiting_waybill"].includes(r.status));
    const rolling = active.filter((r) => ["issued", "awaiting_waybill"].includes(r.status));
    const fuelInTransit = rolling.reduce((s, r) => s + (r.fuel_approved_l || 0), 0);
    const since24h = Date.now() - 24 * 3600 * 1000;
    const doneToday = filteredRows.filter((r) => r.status === "completed" && new Date(rowLastUpdate(r)).getTime() >= since24h).length;
    return {
      activeTrips: active.length,
      rolling: rolling.length,
      fuelInTransit,
      doneToday,
    };
  }, [filteredRows]);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        title="Control Tower"
        subtitle="Live operations · all active trips at a glance"
        action={
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={goCreate}
              data-testid="ct-new-activity-button"
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold bg-ps-blue text-white rounded-md hover:bg-ps-deep"
            >
              <Plus size={14} weight="bold" />
              New activity
            </button>
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="ct-autorefresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="accent-ps-blue"
              />
              <span>Auto-refresh every 30s</span>
            </label>
            <button
              type="button"
              onClick={() => load()}
              data-testid="ct-refresh-button"
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold border border-slate-300 rounded-md hover:bg-white"
            >
              <ArrowsClockwise size={14} weight="bold" />
              Refresh
            </button>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="px-8 py-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ct-kpis">
        <KpiCard label="Active trips" value={kpis.activeTrips} hint="Open work (requested → awaiting waybill)" testid="ct-kpi-active" />
        <KpiCard label="On the road" value={kpis.rolling} hint="Fuel issued, truck rolling" testid="ct-kpi-rolling" tone="amber" />
        <KpiCard label="Fuel in transit" value={litres(kpis.fuelInTransit)} hint="Approved litres not yet reconciled" testid="ct-kpi-fuel" tone="blue" />
        <KpiCard label="Completed (24 h)" value={kpis.doneToday} hint="Closed trips since yesterday" testid="ct-kpi-done" tone="lime" />
      </div>

      {/* Live Fleet Tracking — surfaces every GPS-tracked truck in scope */}
      <FleetTracker gpsByPlate={gpsByPlate} filterParam={filterParam} isAll={isAll} />
      <FleetMap positions={Object.values(gpsByPlate).filter((p) => !filterParam || p.country === filterParam)} />

      {/* Filter bar */}
      <div className="px-8 pb-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search truck, client, driver, route…"
            data-testid="ct-search"
            className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm focus:border-ps-blue focus:outline-none"
          />
        </div>
        <span className="text-slate-500" data-testid="ct-last-refresh">
          <Clock size={12} className="inline mr-1 mb-0.5" />
          Last refresh {lastRefresh ? lastRefresh.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
        </span>
        {loading && (
          <span className="inline-flex items-center gap-1 text-ps-blue text-xs">
            <CircleNotch size={12} className="animate-spin" />
            Loading…
          </span>
        )}
        <span className="text-slate-400 ml-auto">
          {Object.values(grouped).reduce((s, arr) => s + arr.length, 0)} on board · {filteredRows.length} total · scope {scope}
        </span>
      </div>

      {/* Kanban columns */}
      <div className="px-8 pb-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="ct-board">
        {COLUMNS.map((col) => {
          const items = grouped[col.key] || [];
          return (
            <div key={col.key} className="bg-white border border-slate-200 rounded-md min-h-[200px] flex flex-col" data-testid={`ct-col-${col.key}`}>
              <div className={`px-4 py-3 border-b ${col.headerCls} flex items-center gap-2`}>
                <col.icon size={16} weight="duotone" />
                <div className="flex-1">
                  <div className="font-semibold text-sm uppercase tracking-wider">{col.label}</div>
                  <div className="text-[10px] opacity-80 leading-tight">{col.hint}</div>
                </div>
                <span className="px-2 py-0.5 bg-white/60 text-[11px] font-bold rounded border border-current/30" data-testid={`ct-col-${col.key}-count`}>{items.length}</span>
              </div>
              <div className="p-3 flex-1 space-y-2 overflow-y-auto max-h-[70vh]">
                {items.length === 0 && (
                  <div className="text-center text-slate-400 text-xs py-6" data-testid={`ct-col-${col.key}-empty`}>
                    {col.key === "needs_action" ? "All caught up." : "Nothing here."}
                  </div>
                )}
                {items.map((r) => (
                  <TripCard
                    key={r.id}
                    r={r}
                    borderCls={col.border}
                    isAll={isAll}
                    canApprove={canApprove}
                    onAction={startAction}
                    onMoreFuel={goMoreFuel}
                    onViewTrail={(a) => setTrailActivityId(a.id)}
                    gps={gpsByPlate[r.vehicle_number]}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Inline action modal — approve / reject / cancel / trips reconcile / waybill */}
      {actionState && (
        <ActivityActionsModal
          activity={actionState.activity}
          mode={actionState.mode}
          gps={gpsByPlate[actionState.activity?.vehicle_number]}
          onClose={closeAction}
          onDone={async () => { await load(); }}
        />
      )}
      {trailActivityId && (
        <TripGpsTrailModal activityId={trailActivityId} onClose={() => setTrailActivityId(null)} />
      )}
    </div>
  );
}

function KpiCard({ label, value, hint, testid, tone = "default" }) {
  const toneCls = {
    default: "border-slate-200",
    amber:   "border-amber-200 bg-amber-50",
    blue:    "border-sky-200 bg-sky-50",
    lime:    "border-lime-200 bg-lime-50",
  }[tone];
  return (
    <div className={`border ${toneCls} rounded-md px-4 py-3 bg-white`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-display font-extrabold text-ps-deep mt-0.5 font-mono-data">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
    </div>
  );
}

function FleetTracker({ gpsByPlate, filterParam, isAll }) {
  const items = Object.values(gpsByPlate).filter((p) => !filterParam || (p.country === filterParam));
  const [historyToday, setHistoryToday] = useState(0);
  const [replayFor, setReplayFor] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/gps/history/summary").then(({ data }) => { if (alive) setHistoryToday(data.today || 0); }).catch(() => {});
    return () => { alive = false; };
  }, [items.length]);
  if (items.length === 0) return null;
  return (
    <div className="px-8 pb-3" data-testid="ct-fleet-tracker">
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
          <span className="inline-flex w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-bold text-ps-deep">Live Fleet Tracking</h3>
          <span className="text-[10px] text-slate-400">{items.length} truck{items.length === 1 ? "" : "s"} reporting</span>
          {historyToday > 0 && (
            <span className="ml-auto text-[10px] text-slate-500 font-mono-data" title="GPS snapshots recorded today across all trucks" data-testid="ct-history-today">
              📈 {historyToday} snapshots logged today
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-slate-100">
          {items.map((p) => (
            <a
              key={`${p.source}-${p.vehicle_number}`}
              href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`ct-fleet-${p.vehicle_number}`}
              className="bg-white px-3 py-2 hover:bg-slate-50 transition-colors relative group"
            >
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReplayFor(p.vehicle_number); }}
                data-testid={`ct-fleet-${p.vehicle_number}-replay`}
                title="Route replay for today"
                className="absolute top-1 right-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-ps-blue hover:text-white"
              >
                Replay
              </button>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  {isAll && <span className="text-xs">{countryInfo(p.country).flag}</span>}
                  <span className="font-mono-data text-ps-blue font-bold text-sm truncate">{p.vehicle_number}</span>
                  {p.unmatched && <span className="text-[8px] uppercase tracking-wider text-amber-700 bg-amber-50 px-1 rounded border border-amber-200" title="Not yet linked to a truck record in your fleet">unlinked</span>}
                </div>
                <span className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded font-semibold uppercase tracking-wider text-[9px] ${p.online ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  <span className={`w-1 h-1 rounded-full ${p.online ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                  {p.online ? "live" : "off"}
                </span>
              </div>
              <div className="text-[11px] text-slate-700 truncate mt-0.5" title={p.address}>
                {p.address || "—"}
              </div>
              <div className="mt-1 flex items-center gap-3 text-[10px] text-slate-500 font-mono-data">
                <span title="Speed">{typeof p.speed_kmh === "number" ? `${p.speed_kmh} km/h` : "—"}</span>
                {typeof p.fuel_level_pct === "number" && (
                  <span className={`flex items-center gap-0.5 ${p.fuel_level_pct < 25 ? "text-ps-red font-bold" : ""}`} title="Fuel level">⛽ {p.fuel_level_pct}%</span>
                )}
                {p.engine && <span className="uppercase">{p.engine}</span>}
                <span className="ml-auto text-slate-400" title="Last position update">{p.last_seen || "—"}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
      {replayFor && <RouteReplayModal vehicleNumber={replayFor} onClose={() => setReplayFor(null)} />}
    </div>
  );
}

function TripCard({ r, borderCls, isAll, canApprove, onAction, onMoreFuel, onViewTrail, gps }) {
  const chip = STATUS_CHIPS[r.status] || STATUS_CHIPS.requested;
  const updated = rowLastUpdate(r);
  const country = isAll ? countryInfo(r.country) : null;

  // Quick-action bar — content varies by status
  const showApprove   = canApprove && r.status === "requested";
  const showReject    = canApprove && r.status === "requested";
  const showCancel    = canApprove && ["approved", "issued", "awaiting_waybill"].includes(r.status);
  const showTrips     = canApprove && ["approved", "issued", "awaiting_waybill", "completed"].includes(r.status);
  const showWaybill   = canApprove && r.status === "awaiting_waybill";
  const showMoreFuel  = r.status === "completed";

  return (
    <div
      data-testid={`ct-card-${r.id}`}
      className={`border border-slate-200 border-l-4 ${borderCls} rounded-md hover:shadow-sm transition-shadow bg-white`}
    >
      <Link
        to={`/activities?focus=${r.id}`}
        className="block px-3 pt-2.5 pb-2 hover:bg-slate-50 transition-colors rounded-t-md"
        data-testid={`ct-card-${r.id}-body`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {country && <span title={country.name}>{country.flag}</span>}
            <span className="font-mono-data text-ps-blue font-bold text-sm truncate" data-testid={`ct-card-${r.id}-plate`}>{r.vehicle_number}</span>
          </div>
          <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${chip.cls} shrink-0`}>{chip.label}</span>
        </div>
        <div className="mt-1 font-semibold text-ps-deep text-[13px] truncate" title={r.client}>{r.client || "—"}</div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-0.5">
          <span className="uppercase font-semibold tracking-wider">{TYPE_LABEL[r.activity_type] || r.activity_type}</span>
          {(r.origin || r.destination) && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-ps-blue font-semibold truncate" title={`${r.origin || "—"} → ${r.destination || "—"}`}>
                {r.origin || "—"}
                <ArrowRight size={9} className="inline mx-0.5 mb-0.5" />
                {r.destination || "—"}
              </span>
            </>
          )}
        </div>
        {r.lane_name_snapshot && (
          <div className="text-[10px] text-slate-400 truncate mt-0.5" title={r.lane_name_snapshot}>{r.lane_name_snapshot}</div>
        )}
        <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-600 gap-2">
          <span className="truncate" title={r.driver_name}>{r.driver_name || "no driver"}</span>
          <span className="font-mono-data tabular shrink-0" data-testid={`ct-card-${r.id}-fuel`}>
            {r.fuel_actual_l != null && r.fuel_approved_l ? (
              // Reconciled with an actual figure — show issued → used with colour hint
              <>
                <span className="text-slate-500">{num(r.fuel_approved_l)}</span>
                <span className="text-slate-400 mx-0.5">→</span>
                <span className={r.fuel_actual_l > r.fuel_approved_l ? "text-ps-red font-bold" : (r.fuel_actual_l < r.fuel_approved_l ? "text-lime-700 font-bold" : "text-ps-deep font-bold")} title={r.fuel_actual_l > r.fuel_approved_l ? "Burned MORE than issued" : (r.fuel_actual_l < r.fuel_approved_l ? "Burned less than issued" : "Matches issued")}>
                  {num(r.fuel_actual_l)}&nbsp;L used
                </span>
              </>
            ) : r.fuel_approved_l ? `${num(r.fuel_approved_l)} L` : (r.fuel_requested_l ? `${num(r.fuel_requested_l)} L req` : "—")}
          </span>
        </div>
        <div className="mt-1 text-[10px] text-slate-400 flex items-center gap-1">
          <Clock size={10} />
          <span>{relativeTime(updated)}</span>
        </div>
        {gps && (
          <div className="mt-1 pt-1 border-t border-slate-100 flex items-center gap-1.5 text-[10px]" data-testid={`ct-card-${r.id}-gps`} title={`GPS · ${gps.address || "—"} · last seen ${gps.last_seen || "—"}`}>
            <span className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded font-semibold uppercase tracking-wider text-[9px] ${gps.online ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              <span className={`w-1 h-1 rounded-full ${gps.online ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
              GPS
            </span>
            {typeof gps.speed_kmh === "number" && (
              <span className="font-mono-data text-slate-500">{gps.speed_kmh} km/h</span>
            )}
            {typeof gps.fuel_level_pct === "number" && (
              <span className={`font-mono-data ${gps.fuel_level_pct < 25 ? "text-ps-red font-bold" : "text-slate-500"}`} title="Fuel level">⛽ {gps.fuel_level_pct.toFixed(1)}%</span>
            )}
            <span className="truncate text-slate-500 flex-1" title={gps.address}>{gps.address || ""}</span>
          </div>
        )}
      </Link>

      {/* Quick action bar */}
      {(showApprove || showReject || showCancel || showTrips || showWaybill || showMoreFuel) && (
        <div className="px-2 pb-2 pt-1 flex flex-wrap gap-1 border-t border-slate-100" data-testid={`ct-card-${r.id}-actions`}>
          {showApprove && (
            <button onClick={() => onAction(r, "approve")} data-testid={`ct-card-${r.id}-approve`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-ps-blue text-white hover:bg-ps-deep">
              <Check size={11} weight="bold" /> Approve
            </button>
          )}
          {showReject && (
            <button onClick={() => onAction(r, "reject")} data-testid={`ct-card-${r.id}-reject`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-red-300 text-ps-red hover:bg-red-50">
              <X size={11} weight="bold" /> Reject
            </button>
          )}
          {showTrips && (
            <button onClick={() => onAction(r, "trips")} data-testid={`ct-card-${r.id}-trips`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-lime-600 text-white hover:bg-lime-700">
              <Check size={11} weight="bold" /> {r.status === "completed" ? "Edit trips" : "Reconcile"}
            </button>
          )}
          {showWaybill && (
            <button onClick={() => onAction(r, "waybill")} data-testid={`ct-card-${r.id}-waybill`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700">
              <FileText size={11} weight="bold" /> Waybill in
            </button>
          )}
          {showCancel && (
            <button onClick={() => onAction(r, "cancel")} data-testid={`ct-card-${r.id}-cancel`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50">
              <X size={11} weight="bold" /> Cancel
            </button>
          )}
          {showMoreFuel && (
            <button onClick={() => onMoreFuel(r)} data-testid={`ct-card-${r.id}-more-fuel`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-amber-500 text-white hover:bg-amber-600">
              <Plus size={11} weight="bold" /> More fuel
            </button>
          )}
          {onViewTrail && ["issued", "awaiting_waybill", "completed"].includes(r.status) && (
            <button onClick={() => onViewTrail(r)} data-testid={`ct-card-${r.id}-view-trail`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-ps-blue text-ps-blue hover:bg-sky-50">
              <MapPin size={11} weight="bold" /> GPS trail
            </button>
          )}
        </div>
      )}
    </div>
  );
}
